import React, { useState } from 'react';

import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { useAuth } from 'context/auth-context';

import DeleteDocumentModal from './components/DeleteTemplateModal';
import DocumentTemplatesPageHeader from './components/DocumentTemplatesPageHeader';
import DocumentTemplatesTable from './components/DocumentTemplatesTable';
import DrawerContractTemplateForm from './components/DrawerContractTemplateForm';
import DrawerWaiverTemplateForm from './components/DrawerWaiverTemplateForm';
import { DocumentTemplateSearchFilters } from './types';

const DocumentTemplates = (): JSX.Element => {
  const initialFilterValues = {
    search: '',
    type: null,
  };
  const { hasPerm } = useAuth();
  const documentsPerm = hasPerm('DocumentTemplateMutate');

  const [searchFilters, setSearchFilters] = useState<DocumentTemplateSearchFilters>(initialFilterValues);
  const [debouncedSearchFilters] = useDebouncedValue<DocumentTemplateSearchFilters>(searchFilters, 250);
  const [isWaiverFormOpened, { toggle: handleWaiverFormToggle }] = useDisclosure(false);
  const [isContractFormOpened, { toggle: handleContractFormToggle }] = useDisclosure(false);
  const [isDeleteModalOpened, { toggle: handleDeleteModalToggle }] = useDisclosure(false);
  const [documentId, setDocumentId] = useState<string | null>(null);

  const openWaiverForm = (): void => {
    if (!isWaiverFormOpened) {
      handleWaiverFormToggle();
    }
  };

  const openContractForm = (): void => {
    if (!isContractFormOpened) {
      handleContractFormToggle();
    }
  };

  const closeWaiverForm = (): void => {
    setDocumentId(null);
    if (isWaiverFormOpened) {
      handleWaiverFormToggle();
    }
  };

  const closeContractForm = (): void => {
    setDocumentId(null);
    if (isContractFormOpened) {
      handleContractFormToggle();
    }
  };

  const handleDeleteDocument = (id: string): void => {
    setDocumentId(id);
    if (!isDeleteModalOpened) {
      handleDeleteModalToggle();
    }
  };

  const closeDeleteModal = (): void => {
    setDocumentId(null);
    if (isDeleteModalOpened) {
      handleDeleteModalToggle();
    }
  };

  const handleEditDocument = (type: 'waiver' | 'contract', id: string): void => {
    setDocumentId(id);
    if (type === 'waiver') {
      openWaiverForm();
    }
    if (type === 'contract') {
      openContractForm();
    }
  };

  return (
    <>
      <DocumentTemplatesPageHeader
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
        initialFilterValues={initialFilterValues}
        openContractForm={openContractForm}
        openWaiverForm={openWaiverForm}
        permission={documentsPerm}
      />

      <DocumentTemplatesTable
        searchFilters={debouncedSearchFilters}
        handleEditDocument={handleEditDocument}
        handleDeleteDocument={handleDeleteDocument}
        permission={documentsPerm}
      />

      <DrawerWaiverTemplateForm
        formId='waiver-template-form'
        opened={isWaiverFormOpened}
        documentId={documentId}
        closeHandler={closeWaiverForm}
        permission={documentsPerm}
      />

      <DrawerContractTemplateForm
        formId='contract-template-form'
        opened={isContractFormOpened}
        documentId={documentId}
        closeHandler={closeContractForm}
        permission={documentsPerm}
      />

      <DeleteDocumentModal
        documentId={documentId}
        opened={isDeleteModalOpened}
        closeHandler={closeDeleteModal}
      />
    </>
  );
};

export default DocumentTemplates;
